import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import Button from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/badge";
import { deleteUserPortfolio, useAppDispatch } from "../../../redux";
import { useTranslation } from "react-i18next";

interface TechnologyData {
    id?: string;
    title: string;
    link?: string;
    description: string;
    technology: string[];
}

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: (data: TechnologyData[]) => void;
    initialData?: TechnologyData[] | null;
}

const emptyEntry = (): TechnologyData => ({
    title: "",
    link: "",
    description: "",
    technology: [],
});

const PortfolioModal: React.FC<Props> = ({ open, onClose, onSave, initialData }) => {
    const { t } = useTranslation();
    const [entryList, setEntryList] = useState<TechnologyData[]>([]);
    const [techInputs, setTechInputs] = useState<string[]>([]);
    const [errors, setErrors] = useState<Record<number, { [key: string]: string }>>({});
    const dispatch = useAppDispatch();
    useEffect(() => {
        if (initialData?.length > 0) {
            setEntryList(initialData);
            setTechInputs(initialData.map(() => ""));
        } else {
            setEntryList([emptyEntry()]);
            setTechInputs([""]);
        }
        setErrors({});
    }, [open, initialData]);

    const handleFieldChange = (index: number, field: string, value: string) => {
        setEntryList(prev => {
            const updated = [...prev];
            const entry = { ...updated[index] };
            entry[field] = value;
            updated[index] = entry;
            return updated;
        });
        // validate();
    };

    const handleAddTech = (index: number) => {
        const input = techInputs[index].trim();
        if (!input) return;

        setEntryList(prev => {
            const updated = [...prev];
            const updatedEntry = { ...updated[index] };

            // Clone the technology array and add the new tech
            const updatedTech = [...updatedEntry.technology, input];
            updatedEntry.technology = updatedTech;

            updated[index] = updatedEntry;
            return updated;
        });

        setTechInputs(prev => {
            const newInputs = [...prev];
            newInputs[index] = "";
            return newInputs;
        });

        // validate();
    };

    const handleRemoveTech = (entryIdx: number, techIdx: number) => {
        setEntryList(prev => {
            const updated = [...prev];
            const updatedEntry = { ...updated[entryIdx] };

            // Clone the technology array before mutating
            const updatedTech = [...updatedEntry.technology];
            updatedTech.splice(techIdx, 1);

            updatedEntry.technology = updatedTech;
            updated[entryIdx] = updatedEntry;

            return updated;
        });
    };

    const handleAddEntry = () => {
        setEntryList([...entryList, emptyEntry()]);
        setTechInputs([...techInputs, ""]);
    };

    const handleRemoveEntry = async (index: number) => {
        const portfolio = entryList[index];
        if (portfolio?.id) {
            await dispatch(deleteUserPortfolio({ id: portfolio.id }));
        }
        const updated = [...entryList];
        updated.splice(index, 1);
        setEntryList(updated);

        const updatedInputs = [...techInputs];
        updatedInputs.splice(index, 1);
        setTechInputs(updatedInputs);
    };

    const validate = (): boolean => {
        const validationErrors: Record<number, { [key: string]: string }> = {};

        entryList.forEach((entry, index) => {
            const errs: { [key: string]: string } = {};

            if (!entry.title?.trim()) errs.title = t("portfolioModal.titleRequired");
            if (!entry.description?.trim()) errs.description = t("portfolioModal.descriptionRequired");
            if (!entry.technology || entry.technology.length === 0)
                errs.technology = t("portfolioModal.technologyRequired");

            if (Object.keys(errs).length > 0) validationErrors[index] = errs;
        });

        setErrors(validationErrors);
        return Object.keys(validationErrors).length === 0;
    };

    const handleSubmit = e => {
        e.preventDefault();
        if (!validate()) return;
        onSave(entryList);
        onClose();
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-auto">
            <div className="bg-white p-6 rounded-lg w-full max-w-2xl shadow-lg relative max-h-[80vh] overflow-y-auto">
                <button className="absolute top-3 right-3" onClick={onClose}>
                    <X className="w-5 h-5" />
                </button>

                <h2 className="text-xl font-semibold mb-4">{t("portfolioModal.title")}</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <Button type="button" onClick={handleAddEntry} className="mb-4">
                        {t("portfolioModal.addNewItem")}
                    </Button>
                    {entryList.map((entry, idx) => (
                        <div key={idx} className="border rounded-lg p-4 space-y-4">
                            <div className="flex justify-between items-start">
                                <h4 className="font-medium">{t("portfolioModal.portfolioItem")} {idx + 1}</h4>
                                <button
                                    type="button"
                                    onClick={() => handleRemoveEntry(idx)}
                                    className="text-gray-400 hover:text-gray-600">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t("portfolioModal.titleLabel")}</label>
                                <input
                                    type="text"
                                    value={entry.title}
                                    onChange={e => handleFieldChange(idx, "title", e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                {errors[idx]?.title && <p className="text-red-500 text-sm">{errors[idx].title}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t("portfolioModal.description")}</label>
                                <textarea
                                    value={entry.description}
                                    onChange={e => handleFieldChange(idx, "description", e.target.value)}
                                    rows={3}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                                {errors[idx]?.description && (
                                    <p className="text-red-500 text-sm">{errors[idx].description}</p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t("portfolioModal.link")}</label>
                                <input
                                    type="url"
                                    value={entry.link || ""}
                                    onChange={e => handleFieldChange(idx, "link", e.target.value)}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">{t("portfolioModal.technologies")}</label>
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {entry.technology.map((tech, techIdx) => (
                                        <Badge
                                            variant={undefined}
                                            key={techIdx}
                                            className="flex items-center gap-2 bg-blue-50 text-blue-700">
                                            {tech}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveTech(idx, techIdx)}
                                                className="hover:text-blue-900">
                                                <X className="h-3 w-3" />
                                            </button>
                                        </Badge>
                                    ))}
                                </div>
                                <div className="flex gap-2 mt-2">
                                    <input
                                        type="text"
                                        value={techInputs[idx]}
                                        onChange={e => {
                                            const newInputs = [...techInputs];
                                            newInputs[idx] = e.target.value;
                                            setTechInputs(newInputs);
                                        }}
                                        placeholder={t("portfolioModal.addTechnology")}
                                        className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                    <Button
                                        type="button"
                                        onClick={e => {
                                            e.preventDefault();
                                            handleAddTech(idx);
                                        }}>
                                        {t("portfolioModal.add")}
                                    </Button>
                                </div>
                                {errors[idx]?.technology && (
                                    <p className="text-red-500 text-sm mt-1">{errors[idx].technology}</p>
                                )}
                            </div>
                        </div>
                    ))}
                    <div className="flex justify-end gap-4">
                        <Button type="button" variant="outline" onClick={onClose}>
                            {t("portfolioModal.cancel")}
                        </Button>
                        <Button type="submit">{t("portfolioModal.saveChanges")}</Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PortfolioModal;
